import { IconCopy } from '@developerz/ui';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type ApiCall, apiOrigin, curlSnippet, mcpEquivalent, mcpSnippet } from '../lib/api-call';
import { urlOrg } from '../lib/org';
import { Button } from './ui/Button';

// "Same call over API / MCP" — the disclosure beside a mutating control that
// shows the exact REST and MCP equivalents of the button next to it.
//
// THIS IS THE PARITY CLAIM, MADE CHECKABLE BY THE READER. The platform promises
// three surfaces over one capability, and `capability-manifest.ts` states that
// per capability for a machine. This is the human's copy of the same answer,
// delivered where they are already standing: a maintainer who wants their CI to
// do what they just did copies the command, and an agent's owner sees the verb
// to hand it. Nobody has to find the docs page, and the docs page cannot go
// stale relative to this, because both snippets are rendered from the very
// `ApiCall` the button fires (`lib/api-call.ts`).
//
// A MISSING MCP HALF IS SHOWN, NOT HIDDEN. Where the capability manifest files
// MCP as `na`/`gap`, or where the verb does not reach THIS direction of the act,
// the block says so and prints the reason instead of a second command. Hiding it
// would make an asymmetry look like a design, which is exactly the failure the
// manifest exists to stop — one indirection down. Disabling a repo was the live
// example until `project disable` landed (#4607).
//
// COLLAPSED BY DEFAULT, native `<details>`: no focus trap, no portal, no
// keyboard handling of our own, and zero cost on a row that nobody opens. The
// same primitive `<AuditTimeline/>` and the CI failure tail already use.

/** One act the host control can perform, labelled as the button labels it. */
export type CallOption = {
  /** Reads as the button does: "Enable", "Revoke", "Drain". */
  readonly label: string;
  readonly call: ApiCall;
};

export function CopyAsCall(props: {
  readonly calls: readonly CallOption[];
  /** Layout modifier for the host surface; the block itself is context-free. */
  readonly class?: string;
}): JSX.Element {
  // Which snippet was copied last, by its `<option index>:<surface>` key. One
  // slot, not one per button: only the last copy is worth acknowledging, and a
  // per-button latch would leave four blocks all claiming "Copied".
  const [copied, setCopied] = createSignal<string | null>(null);
  // Guarded: happy-dom (tests) and non-secure contexts lack navigator.clipboard.
  const copy = (key: string, snippet: string): void =>
    void navigator.clipboard?.writeText(snippet).then(
      () => setCopied(key),
      () => setCopied(null),
    );

  return (
    <details class={props.class ? `copy-as-call ${props.class}` : 'copy-as-call'}>
      <summary class="copy-as-call__summary">
        <IconCopy size="0.75rem" />
        {t('copy_as_call.summary')}
      </summary>

      <div class="copy-as-call__body">
        <p class="field__desc">{t('copy_as_call.token_hint')}</p>

        <For each={props.calls}>
          {(option, index) => {
            const rest = (): string => curlSnippet(option.call);
            // The page's org rides the MCP call as `account` (`lib/api-call.ts`).
            const mcp = (): string | null => mcpSnippet(option.call, apiOrigin(), urlOrg());
            const restKey = (): string => `${index()}:rest`;
            const mcpKey = (): string => `${index()}:mcp`;
            return (
              <section class="copy-as-call__act">
                <p class="copy-as-call__label">{option.label}</p>

                <p class="copy-as-call__surface">{t('copy_as_call.rest_label')}</p>
                <pre class="code-block">{rest()}</pre>
                <Button
                  variant="outline"
                  size="sm"
                  data-copy="rest"
                  onClick={() => copy(restKey(), rest())}
                >
                  <IconCopy size="0.75rem" />
                  {t(copied() === restKey() ? 'copy_as_call.copied' : 'copy_as_call.copy')}
                </Button>

                <p class="copy-as-call__surface">{t('copy_as_call.mcp_label')}</p>
                <Show when={mcp()} fallback={<McpAbsent call={option.call} />} keyed>
                  {(snippet) => (
                    <>
                      <pre class="code-block">{snippet}</pre>
                      <Button
                        variant="outline"
                        size="sm"
                        data-copy="mcp"
                        onClick={() => copy(mcpKey(), snippet)}
                      >
                        <IconCopy size="0.75rem" />
                        {t(copied() === mcpKey() ? 'copy_as_call.copied' : 'copy_as_call.copy')}
                      </Button>
                    </>
                  )}
                </Show>
              </section>
            );
          }}
        </For>
      </div>
    </details>
  );
}

/**
 * Why there is no MCP command. A manifest `na`/`gap` and a call-site refusal both
 * carry their own sentence and render verbatim; anything else is OUR bug (a verb
 * the manifest no longer lists) and gets our own copy, because the reader can do
 * nothing about it and must not be handed a call the server would not answer.
 */
function McpAbsent(props: { readonly call: ApiCall }): JSX.Element {
  const reason = (): string => {
    const verdict = mcpEquivalent(props.call);
    return verdict.kind === 'unavailable'
      ? t('copy_as_call.mcp_unavailable', { reason: verdict.reason })
      : t('copy_as_call.mcp_drift');
  };
  return <p class="copy-as-call__absent">{reason()}</p>;
}
